import {
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getItemsCategories } from 'store/items-categories/actions';
import { getBrowseItems } from 'store/browse-items/selectors';
import {
  getItemsCategoriesFormatted,
  getItemsCategoriesLoadingState,
  getItemsCategoriesOptions,
} from 'store/items-categories/selectors';
import useDebounce from 'library/hooks/use-debounce';
import { getLabelMap } from 'library/helpers/text';
import MiniSearch from 'minisearch';
import { getWishlistItems as getWishlistItemsSelector } from '../../../store/wishlist-items/selectors';
import { getProjectItems as getProjectItemsSelector } from '../../../store/project-items/selectors';

// eslint-disable-next-line import/prefer-default-export
export const DEFAULT_SEARCH_CONFIGURATION = {
  fields: ['text'],
  idField: 'value',
  searchOptions: {
    prefix: true,
  },
};

export const getDefaultSearchResultMap = (result, { value }) => ({
  ...result,
  [value]: true,
}); // => { [item id]: true }

export const getSearchResultMap = (result, { id }) => ({
  ...result,
  [id]: true,
}); // => { [item id]: true }

const useItemsCategoryList = ({
  skip = true,
  defaultSnapshot = {},
}) => {
  const [query, onQueryChange] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const dispatch = useDispatch();
  const data = useSelector(getItemsCategoriesFormatted);
  const isLoading = useSelector(getItemsCategoriesLoadingState);
  const options = useSelector(getItemsCategoriesOptions);
  const items = useSelector(getBrowseItems);
  const wishlistItems = useSelector(getWishlistItemsSelector);
  const projectItems = useSelector(getProjectItemsSelector);

  const labelMapSnapshot = useRef({});
  const search = useMemo(() => new MiniSearch(DEFAULT_SEARCH_CONFIGURATION), [options]);
  const [allowedOptionMap, setAllowedOptions] = useState({});
  // todo - pass 'items' array as a prop?
  const isEntityListEmpty = items.length === 0 && wishlistItems.length === 0 && projectItems.length === 0;

  useEffect(() => {
    if (!isLoading && !isEntityListEmpty && !options.length && !skip) {
      dispatch(getItemsCategories());
    }
  }, [isLoading, skip, options?.length, isEntityListEmpty]);

  useEffect(() => {
    if (options?.length > 0) {
      search.addAll(options);
      setAllowedOptions(options.reduce(getDefaultSearchResultMap, {}));
    }
  }, [options?.length > 0]);

  useEffect(() => {
    if (debouncedQuery) {
      const results = search.search(debouncedQuery);

      setAllowedOptions(results.reduce(getSearchResultMap, {}));
    } else {
      // if search input is empty, allow all options to be displayed
      setAllowedOptions(options.reduce(getDefaultSearchResultMap, {}));
    }
  }, [debouncedQuery]);

  return useMemo(() => {
    const labelMap = getLabelMap(data, {
      ...defaultSnapshot,
      ...labelMapSnapshot.current,
    });

    labelMapSnapshot.current = labelMap;

    return {
      data,
      isLoading,
      options: options.filter(({ value }) => allowedOptionMap[value]),
      labelMap,
      onQueryChange,
    };
  }, [data, defaultSnapshot, skip]);
};

export default useItemsCategoryList;
